import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, SearchLg, Users01 } from '@untitledui/icons';
import { toast } from 'react-toastify';
import { memberService } from '../services/memberService';
import { Avatar } from './untitled-ui/base/avatar/avatar';
import { Input } from './untitled-ui/base/input/input';

interface Member { id: number; fullName: string; email?: string; phone?: string; memberNumber: string; nicPassport?: string; profilePhoto?: string | null; membershipType: string; status: string; }
const initials = (name: string) => name.split(' ').map(part => part[0]).join('').slice(0, 2).toUpperCase();
const titleCase = (value: string) => value.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());

const PaymentMemberSelect: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [members, setMembers] = useState<Member[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    memberService.getMembers({ status: 'ACTIVE', size: 500 })
      .then(response => {
        if (!response.success || !response.data) throw new Error();
        setMembers(response.data.members);
      })
      .catch(() => toast.error('Failed to load members'))
      .finally(() => setIsLoading(false));
  }, []);

  const filteredMembers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return members;
    return members.filter(member => [member.fullName, member.memberNumber, member.nicPassport, member.phone].some(value => value?.toLowerCase().includes(term)));
  }, [members, searchTerm]);

  return (
    <div className="u-page">
      <div className="u-page-header">
        <div><h2>Record a payment</h2><p>Select the member who is making the payment.</p></div>
        <span className="u-header-count"><Users01 />{members.length} active members</span>
      </div>
      <section className="u-panel">
        <div className="u-toolbar"><Input className="u-search" icon={SearchLg} placeholder="Search name, member number, phone or NIC..." value={searchTerm} onChange={setSearchTerm} /></div>
        <div className="u-table-wrap">
          <table className="u-table">
            <thead><tr><th>Member</th><th>Membership</th><th>Phone</th><th>NIC / Passport</th><th aria-label="Select"></th></tr></thead>
            <tbody>
              {isLoading ? <tr><td colSpan={5}><div className="u-state"><span className="spinner" />Loading members…</div></td></tr> :
              filteredMembers.length === 0 ? <tr><td colSpan={5}><div className="u-state"><strong>No members found</strong><span>Try a different search.</span></div></td></tr> :
              filteredMembers.map(member => (
                <tr key={member.id} onClick={() => navigate(`/payments/record/${member.id}`, { state: { member } })}>
                  <td><div className="u-person"><Avatar size="sm" src={member.profilePhoto} alt={member.fullName} initials={initials(member.fullName)} /><span><strong>{member.fullName}</strong><small>#{member.memberNumber} · {member.email || 'No email'}</small></span></div></td>
                  <td><span className="u-badge u-badge-neutral">{titleCase(member.membershipType)}</span></td>
                  <td>{member.phone || '—'}</td><td>{member.nicPassport || '—'}</td>
                  <td><button className="u-row-action" type="button" aria-label={`Record payment for ${member.fullName}`}><ChevronRight /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="u-pagination"><span>{filteredMembers.length} members</span><span>Select a row to continue</span></div>
      </section>
    </div>
  );
};

export default PaymentMemberSelect;
